import { useTranslation } from 'react-i18next'
import type { SettingLookCode } from '@gk/contracts'
import type { LookFrame } from '../data/lookGeometry.ts'
import { choiceOptions, type ParamDef, type TaskTypeRow } from '../data/plan.ts'
import { parseNumber } from '../data/planSetup.ts'
import { Field } from './components.tsx'
import { LookPicker } from './looks/LookPicker.tsx'

/** M25-S (docs/M25.md E, rule 10): ONE FIELD FOR A SETTING, used by the template form, the job form and each rotation step.
 *  - a choice setting whose choices have pictures (the club chose them in Types de travail) is a `LookPicker`: tiles of 56 px with the picture above the club's
 *    word, or a dial of spokes when every choice is a direction; a last tile, « Non précisé », leaves it unset (the value is then the empty text, as the <select>
 *    had it);
 *  - a choice with no picture (or a type that has none) is the <select> it was, a number is typed with a decimal keypad, a text is a text.
 *  The value is always the text the form holds (a choice's word, a number as typed). The field reads only the pictures the club chose, never a word (rule 4).
 *  `previous` is the page's previous-cut picture for the dial (only a page whose viewer holds `worklog.read` gives it). */
export function ParamField({ def, tt, value, onChange, idPrefix, lang, frame = 'round', previous = null, disabled = false }: {
  def: ParamDef; tt: TaskTypeRow | undefined; value: string; onChange: (value: string) => void; idPrefix: string; lang: 'fr' | 'en' | string
  frame?: LookFrame; previous?: SettingLookCode | null; disabled?: boolean
}) {
  const { t } = useTranslation()
  const label = def.label[lang.startsWith('en') ? 'en' : 'fr'] + (def.unit ? ` (${def.unit})` : '')
  const id = `${idPrefix}-${def.key}`
  if (def.kind === 'choice') {
    const options = choiceOptions(tt, def.key)
    if (options.some((o) => o.look !== null)) {
      return <LookPicker legend={label} options={options} value={value === '' ? null : value} onChange={(v) => onChange(v ?? '')} none={t('planSetup.tpl.notSet')} frame={frame} previous={previous} disabled={disabled} testId={id} />
    }
    return (
      <Field label={label} htmlFor={id}>
        <select id={id} className="input" value={value} disabled={disabled} onChange={(e) => onChange(e.target.value)} data-testid={id}>
          <option value="">{t('planSetup.tpl.notSet')}</option>
          {(def.choices ?? []).map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
      </Field>
    )
  }
  return (
    <Field label={label} htmlFor={id} error={def.kind === 'number' && value.trim() && parseNumber(value) === null ? t('planSetup.err.PARAM_VALUE_INVALID') : null}>
      <input id={id} className="input" inputMode={def.kind === 'number' ? 'decimal' : 'text'} maxLength={200} value={value} disabled={disabled} onChange={(e) => onChange(e.target.value)} data-testid={id} />
    </Field>
  )
}
